import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

export interface SegmentedControlOption<T extends string> {
  value: T
  label: string
  /** Greyed out and not selectable. */
  disabled?: boolean
  /** Tooltip, e.g. why an option is disabled. */
  hint?: string
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  'aria-label'?: string
}

/**
 * SegmentedControl
 *
 * In-card mode switcher: title left, this control right. Denser than shadcn
 * Tabs and lighter than a Select — for flipping between list modes without
 * leaving the card (e.g. Pages / Entry Page / Exit Page, Referrals / Sources).
 *
 * Active segment = bg-muted + text-foreground (never orange / primary).
 * Weight stays font-medium — hierarchy comes from the muted fill, not bold.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  const hasHints = options.some((o) => o.hint)
  const control = (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-0.5 overflow-x-auto scrollbar-hide min-w-0 rounded-md border border-border/30 bg-background/40 p-0.5',
        className
      )}
    >
      {options.map((opt) => {
        const isActive = opt.value === value
        const button = (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={opt.disabled || undefined}
            onClick={() => {
              if (!opt.disabled) onChange(opt.value)
            }}
            className={cn(
              'h-6 shrink-0 whitespace-nowrap rounded-[5px] px-2.5 text-xs font-medium transition-colors',
              isActive
                ? 'bg-muted text-foreground'
                : opt.disabled
                  ? 'cursor-not-allowed text-muted-foreground/45'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
            )}
          >
            {opt.label}
          </button>
        )
        if (!opt.hint) return button
        return (
          <Tooltip key={opt.value}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>{opt.hint}</TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
  return hasHints ? <TooltipProvider delayDuration={150}>{control}</TooltipProvider> : control
}
